import React, { useCallback, useMemo } from 'react';
import { useEvent } from 'effector-react';

import { ErrorBoundary } from '@src/atoms/ErrorBoundary/ErrorBoundary';
import { useEventEmitter } from '@src/hooks/useEventEmitter';
import { BreakpointsProvider } from '../Breakpoints';
import { IAppContext, IAppProviderProps, TAppNotificationParams } from './models';
import { Portal } from '@src/core/Portal';
import { PopupCookies } from '@src/molecules/PopupCookies';
import { EVENTS_NAME } from '@src/consts/common';
import { AppNotifications } from './_Notification/AppNotification';
import { addNotification } from '../../stores';
import { ExternalAnalytics } from '@src/core/ExternalAnalytics';
import { AuthProvider } from '../Auth';
import { AppContext } from '@centra/ui-global-context';
import styled from './AppProvider.scss';
import { getDevelopmentMode } from './utils';
import { AnyType } from '@src/models/common';

/**
 * Ключевой провайдер которым необходимо оборачивать каждый bundle на самом верхнем уровне, содержит данные настройки и т.д.
 *
 * !!!!!!!!
 * Важно: компоненты библиотеки для внешних запросов должны использовать NgApiClient из контекста AppContext,
 * т.к. именно он принимает внешний конфиг из каждого конкретного бандла
 * !!!!!!!!
 */
export const AppProvider: React.FC<IAppProviderProps> = ({
  ngApiClient,
  locale = 'RU-ru',
  formFactor,
  children,
  isAuthZone = false,
  gtmContainerId,
  pagesCache,
  nextComponent,
  experimentsStore,
  isShowElemIdsInsteadChildren,
}) => {
  ngApiClient.setIsAuthRequest(isAuthZone);
  const notificationAddEvent = useEvent(addNotification);
  const emitter = useEventEmitter();

  const handleFallback = useCallback(
    (error: Error, errorInfo: React.ErrorInfo) => {
      emitter.emit(EVENTS_NAME.UNIT_EVENT_LOG, {
        details: { dataId: '$self', kind: 'error' },
        error: { errorInfo, error },
      });
    },
    [emitter],
  );

  const handleOpenNotification = useCallback(
    (data: TAppNotificationParams) => {
      notificationAddEvent(data);
    },
    [notificationAddEvent],
  );

  const mode = getDevelopmentMode(ngApiClient);

  const value = useMemo<IAppContext>(
    () => ({
      pagesCache,
      ngApiClient,
      locale,
      isAuthZone,
      gtmContainerId,
      nextComponent,
      setNotification: handleOpenNotification,
      experimentsStore,
      isShowElemIdsInsteadChildren,
      mode,
    }),
    [
      pagesCache,
      ngApiClient,
      locale,
      isAuthZone,
      gtmContainerId,
      nextComponent,
      handleOpenNotification,
      experimentsStore,
      isShowElemIdsInsteadChildren,
      mode,
    ],
  );

  if (typeof window !== 'undefined' && value.mode === 'dev') {
    window.__CENTRA__ = value as AnyType;
  }

  return (
    <AppContext.Provider value={value}>
      <AuthProvider isAuthZone={isAuthZone}>
        {value.gtmContainerId && <ExternalAnalytics gtmContainerId={value.gtmContainerId} />}
        <BreakpointsProvider formFactor={formFactor} onlyInit>
          <ErrorBoundary fallback={handleFallback}>{children}</ErrorBoundary>
          <AppNotifications mode={mode} />
          <Portal>
            <div className={styled.bottomContainer}>{!isAuthZone && <PopupCookies />}</div>
          </Portal>
        </BreakpointsProvider>
      </AuthProvider>
    </AppContext.Provider>
  );
};
